import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { toast } from 'react-toastify';
import styled from 'styled-components';

import { PAGES } from 'constants/pages';

import { Header, HeaderTitle } from 'components/Header';
import { Loader } from 'components/Loader';
import { PageTitle } from 'components/PageTitle';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { AuditLogsSection } from 'components/AuditLogs/AuditLogsSection';

import { EquipmentFormContent } from './components/EquipmentFormContent';
import { StatesSection } from './components/StatesSection';

import {
    useGetDetailedEquipmentQuery,
    useGetSuppliersQuery,
    useUpdateEquipmentMutation,
    TotadminPermission,
} from 'data/__generated__';

type FormData = {
    name: string;
    brand: string;
    volume: string;
    price: number;
    description: string;
    hidden: boolean;
    height: number;
    width: number;
    length: number;
    weight: number;
    image?: File | null;
    supplierId?: string | null;
};

export const EquipmentDetails = () => {
    const { equipmentId = '' } = useParams<{
        equipmentId: string;
    }>();

    const {
        loading: equipmentLoading,
        data: equipmentData,
        error: equipmentError,
    } = useGetDetailedEquipmentQuery({
        variables: { equipmentId },
    });

    const { data: suppliersData } = useGetSuppliersQuery();

    const [updateEquipment, { loading: updateEquipmentLoading }] = useUpdateEquipmentMutation();

    const methods = useForm<FormData>();
    const {
        handleSubmit,
        formState: { isDirty },
        reset,
    } = methods;

    if (equipmentLoading) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (!equipmentData || equipmentError) {
        throw new Error('Une erreur est survenue lors de la récupération du rituel');
    }

    const onSubmit = handleSubmit(async (fields) => {
        const { data } = await updateEquipment({
            variables: {
                equipmentId,
                updateFields: fields,
            },
        });
        if (data) {
            const {
                updateEquipment: { equipment, success, error },
            } = data;
            if (success && equipment) {
                toast.success('Le rituel a bien été mis à jour !');
                // Drop the uploaded File from the form state so a subsequent save does not
                // re-upload it; the refreshed imageUrl comes from the updated Apollo cache.
                reset({ ...fields, image: undefined });
            } else {
                if (error) {
                    toast.error(error, { autoClose: false });
                } else {
                    throw Error("Une erreur inconnue s'est produite");
                }
            }
        } else {
            throw Error("Une erreur inconnue s'est produite");
        }
    });

    const { detailedEquipment: equipment } = equipmentData;

    if (!equipment) {
        throw new Error("Une erreur est survenue lors de la récupération d'équipement");
    }

    const supplierOptions = (suppliersData?.suppliers ?? []).map(({ _id, name }) => ({
        label: name ?? '',
        value: _id,
    }));

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: '/equipments' }],
        },
    ];

    return (
        <Container>
            <FormProvider {...methods}>
                <Form onSubmit={onSubmit}>
                    <Header>
                        <HeaderTitle>
                            <PageTitle page={PAGES.equipmentDetails} />
                        </HeaderTitle>
                        <ActionsAndSave
                            actionGroups={actionGroups}
                            onSave={onSubmit}
                            saveDisabled={!isDirty}
                            saveLoading={updateEquipmentLoading}
                            saveTitle="Mettre à jour"
                            saveRequiredPermissions={[TotadminPermission.EquipmentsManage]}
                        />
                    </Header>
                    <Content>
                        <ScrollableContent>
                            <EquipmentFormContent equipment={equipment} supplierOptions={supplierOptions} />
                            <StatesSection equipment={equipment} isDirty={isDirty} />
                            <AuditLogsSection collectionDocumentIds={[equipmentId]} />
                        </ScrollableContent>
                    </Content>
                </Form>
            </FormProvider>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ScrollableContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;
`;
